import { INITIAL_TOOL_SYSTEM, type ToolSystem, isToolSystem } from '@/types/toolSystem.types';
import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';

const STORAGE_KEY = 'eda_tool_system';

/** Read the remembered system; a browser that refuses storage simply has none. */
function readStored(): ToolSystem {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isToolSystem(stored) ? stored : INITIAL_TOOL_SYSTEM;
  } catch (error) {
    console.warn('Could not read the remembered tool system:', error);
    return INITIAL_TOOL_SYSTEM;
  }
}

function writeStored(system: ToolSystem): void {
  try {
    localStorage.setItem(STORAGE_KEY, system);
  } catch (error) {
    console.warn('Could not remember the tool system:', error);
  }
}

interface ToolSystemContextValue {
  /** The system every list on the page is scoped to. */
  toolSystem: ToolSystem;
  setToolSystem: (system: ToolSystem) => void;
}

const ToolSystemContext = createContext<ToolSystemContextValue | undefined>(undefined);

/**
 * The tool system the whole app is currently working in.
 *
 * It is a mode, not a filter: projects, rule sets and the tool catalog are all scoped to it,
 * because a project machined with one catalog has nothing to say about the other. It sticks
 * across reloads — a shop works in one system for a whole session, not per page.
 */
export const ToolSystemProvider = ({ children }: { children: ReactNode }) => {
  const [toolSystem, setState] = useState<ToolSystem>(readStored);

  const setToolSystem = useCallback((system: ToolSystem) => {
    setState(system);
    writeStored(system);
  }, []);

  const value = useMemo(() => ({ toolSystem, setToolSystem }), [toolSystem, setToolSystem]);

  return <ToolSystemContext.Provider value={value}>{children}</ToolSystemContext.Provider>;
};

export const useToolSystem = (): ToolSystemContextValue => {
  const context = useContext(ToolSystemContext);
  if (context === undefined) {
    throw new Error('useToolSystem must be used within a ToolSystemProvider');
  }
  return context;
};
